import React, { useState, useEffect } from "react";
import { X, Award, Lock, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";

const GOLD = { border: "rgba(193, 166, 97, 0.35)", glow: "rgba(193, 166, 97, 0.18)" };

export const ClassificationModal = ({ open, onClose, onSubscribe }) => {
  const { lang, t } = useLang();
  const { subscribed } = useSubscription();
  const { user } = useAuth();
  const isSubscribed = true; // free access — no subscription required
  const [zoomSrc, setZoomSrc] = useState(null);

  useEffect(() => { if (!open) setZoomSrc(null); }, [open]);
  if (!open) return null;

  const pages = lang === "en"
    ? ["/fiches/classification/general-en-p1.png", "/fiches/classification/general-en-p2.png"]
    : ["/fiches/classification/general-fr-p1.png", "/fiches/classification/general-fr-p2.png"];
  const heading = lang === "fr" ? "Classification générale" : "General Classification";
  const subhead = lang === "fr"
    ? "Fiche technique gemmologique — qualités AAA / AA / A pour saphirs, rubis et émeraudes"
    : "Gemological technical sheet — AAA / AA / A qualities for sapphires, rubies and emeralds";

  return (
    <div
      data-testid="classification-modal"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl my-6 rounded-sm bg-[#050B1E] border p-4 md:p-10"
        style={{ borderColor: GOLD.border, boxShadow: `inset 0 0 120px ${GOLD.glow}` }}
      >
        <button
          data-testid="classification-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors z-10"
          aria-label={t.modal.close}
        >
          <X size={22} strokeWidth={1.4} />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Award size={22} strokeWidth={1.4} className="text-[#C1A661]" />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661]">
              {lang === "fr" ? "Fiche technique" : "Technical sheet"}
            </div>
            <h2 className="font-serif text-2xl md:text-4xl font-light text-[#F8F5EE] leading-tight">
              {heading}
            </h2>
            <p className="mt-1 text-[12px] text-[#A1A7B3] font-light italic">{subhead}</p>
          </div>
        </div>

        {isSubscribed ? (
          <div className="space-y-6" data-testid="classification-content">
            {pages.map((src, i) => (
              <div key={src} className="space-y-2">
                <button
                  type="button"
                  onClick={() => setZoomSrc(src)}
                  className="block w-full text-left"
                  aria-label={t.fiche.viewFullSize}
                >
                  <img
                    src={src}
                    alt={`${heading} — page ${i + 1}`}
                    data-testid={`classification-page-${i + 1}`}
                    className="w-full h-auto rounded-sm border cursor-zoom-in hover:brightness-105 transition"
                    style={{ borderColor: GOLD.border, backgroundColor: "#F8F5EE" }}
                    loading="lazy"
                  />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setZoomSrc(pages[0])}
              className="inline-block text-[10px] tracking-[0.25em] uppercase text-[#C1A661] hover:text-[#E2CFA4] transition-colors"
            >
              {t.fiche.viewFullSize} ↗
            </button>
          </div>
        ) : (
          <div className="relative border border-[#C1A661]/20 rounded-sm overflow-hidden">
            <img
              src={pages[0]}
              alt={heading}
              className="w-full h-auto filter blur-md select-none pointer-events-none"
              style={{ backgroundColor: "#F8F5EE" }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#050B1E]/70 backdrop-blur-sm p-6 text-center">
              <Lock size={28} strokeWidth={1.4} className="text-[#C1A661] mb-3" />
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C1A661] font-medium mb-2">
                {t.fiche.lockedTitle}
              </div>
              <p className="text-sm text-[#D8DBE2] font-light max-w-md mb-5">
                {t.fiche.lockedBody}
              </p>
              <button
                data-testid="classification-subscribe-btn"
                onClick={onSubscribe}
                className="px-6 py-3 rounded-full bg-[#C1A661] hover:bg-[#E2CFA4] text-[#050B1E] text-xs tracking-[0.25em] uppercase font-semibold transition-colors"
              >
                {t.fiche.lockedCta}
              </button>
            </div>
          </div>
        )}
      </div>

      {zoomSrc && (
        <div
          data-testid="classification-zoom-overlay"
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#C1A661]/20 bg-[#050B1E]/95">
            <button
              data-testid="classification-zoom-back-btn"
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] text-[#E2CFA4] hover:text-[#F8F5EE] text-xs tracking-[0.25em] uppercase transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              <span>{lang === "fr" ? "Retour" : "Back"}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors"
              aria-label={t.modal.close}
            >
              <X size={22} strokeWidth={1.4} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4 md:p-8 flex items-start justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={zoomSrc}
              alt={heading}
              className="max-w-full h-auto rounded-sm shadow-2xl"
              style={{ backgroundColor: "#F8F5EE" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
